export type SearchablePhrase = {
  bodyText: string;
  name: string;
};

export type TextSelection = {
  start: number;
  end: number;
};

export type PhraseSelectionReplacement = {
  caretPosition: number;
  value: string;
};

const ELLIPSIS = "…";
const RETURN_SYMBOL = "↵";

const truncate = (value: string, maxLength: number): string => {
  if (maxLength <= 0) return "";
  if (value.length <= maxLength) return value;

  return `${value.slice(0, maxLength - ELLIPSIS.length).trimEnd()}${ELLIPSIS}`;
};

export const filterPhrasesBySearch = <TPhrase extends SearchablePhrase>(
  phrases: TPhrase[],
  search: string,
): TPhrase[] => {
  const normalizedSearch = search.trim().toLocaleLowerCase();
  if (!normalizedSearch) return phrases;

  return phrases.filter(({ bodyText, name }) => {
    return (
      name.toLocaleLowerCase().includes(normalizedSearch) ||
      bodyText.toLocaleLowerCase().includes(normalizedSearch)
    );
  });
};

export const getPhraseFirstLinePreview = (bodyText: string, maxLength?: number): string => {
  const singleLineText = bodyText
    .trim()
    .replace(/(?:\r\n|\r|\n)(?:[ \t]*(?:\r\n|\r|\n))*/gu, ` ${RETURN_SYMBOL} `)
    .replace(/\s+/gu, " ");

  if (maxLength !== undefined) {
    return truncate(singleLineText, maxLength);
  }
  return singleLineText;
};

export const replaceSelectionWithPhrase = (
  value: string,
  { start, end }: TextSelection,
  phraseText: string,
): PhraseSelectionReplacement => {
  const prefix = value.slice(0, start);
  const suffix = value.slice(end);
  const leadingSpace = /\S$/u.test(prefix) ? " " : "";
  const trailingSpace = /^\S/u.test(suffix) ? " " : "";
  const insertedText = `${leadingSpace}${phraseText}${trailingSpace}`;

  return {
    caretPosition: prefix.length + insertedText.length, // puts caret at end of inserted text
    value: `${prefix}${insertedText}${suffix}`,
  };
};
